// src/components/charts/BarChart.js
import React, { useMemo, useRef, useState } from 'react';
import './BarChart.css';

export default function BarChart({
  data = [],            // [{ x: '00–04', y: 12, meta }]
  width = 360,
  height = 220,

  // Title row
  title,
  subtitle,
  titleRight,

  yMax,                 // optional
  formatX = (x) => x,
  formatY = (y) => y,
  tooltipFormat,

  // ✅ NEW: render without card look (for full-width detail screens)
  flat = false,
}) {
  const padding = { top: 24, right: 12, bottom: 34, left: 40 };
  const svgRef = useRef(null);

  const [hover, setHover] = useState(null); // { i }

  const { xs, ys, bars, ticksY, computedMax } = useMemo(() => {
    const innerW = Math.max(1, width - padding.left - padding.right);
    const innerH = Math.max(1, height - padding.top - padding.bottom);

    const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

    const xs = data.map((d) => d?.x ?? '');
    const ys = data.map((d) => toNum(d?.y));

    const maxYData = ys.length ? Math.max(...ys) : 0;
    const computedMax = yMax ?? Math.max(5, Math.ceil(maxYData));
    const rangeY = Math.max(1, computedMax);

    const yScale = (vRaw) =>
      padding.top + innerH - (toNum(vRaw) * innerH) / rangeY;

    const n = Math.max(1, xs.length);

    // gap scales a bit with size, but never too small
    const gap = Math.max(6, Math.floor(innerW * 0.04));
    const barW = Math.max(10, Math.floor((innerW - gap * (n - 1)) / n));

    const bars = xs.map((label, i) => {
      const x = padding.left + i * (barW + gap);
      const y = yScale(ys[i]);
      const h = padding.top + innerH - y;
      return { i, label, x, y, w: barW, h, value: ys[i] };
    });

    // Y ticks
    const stepY = Math.max(1, Math.ceil(computedMax / 4));
    const ticksY = [];
    for (let v = 0; v <= computedMax; v += stepY) {
      ticksY.push({ v, y: yScale(v) });
    }

    return { xs, ys, bars, ticksY, computedMax };
  }, [data, width, height, yMax]);

  const onLeave = () => setHover(null);

  const hitTest = (clientX, clientY) => {
    if (!svgRef.current) return null;
    const rect = svgRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    // Only inside plot area
    if (x < padding.left || x > width - padding.right) return null;
    if (y < padding.top || y > height - padding.bottom) return null;

    for (const b of bars) {
      if (x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h) {
        return b.i;
      }
    }
    return null;
  };

  const onMouseMove = (e) => {
    const i = hitTest(e.clientX, e.clientY);
    setHover(i != null ? { i } : null);
  };

  const onTouchMove = (e) => {
    if (!e.touches?.length) return;
    const t = e.touches[0];
    const i = hitTest(t.clientX, t.clientY);
    setHover(i != null ? { i } : null);
  };

  // Tooltip box
  let tipContent = null;
  if (hover) {
    const i = hover.i;
    const d = data[i] || {};
    const x = xs[i];
    const y = ys[i];
    const meta = d.meta;

    const defaultTip = { title: String(x), lines: [`Value: ${formatY(y)}`] };
    const formatted = tooltipFormat ? tooltipFormat({ x, y, meta }) : defaultTip;

    tipContent = (
      <div className="bc-tip-box">
        <div className="bc-tip-title">{formatted.title}</div>
        {formatted.lines?.map((ln, idx) => (
          <div key={idx} className="bc-tip-line">
            {ln}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className={['barchart-wrap', flat ? 'is-flat' : ''].join(' ')}
      style={{ width: flat ? '100%' : width }}
    >
      {(title || titleRight || subtitle) && (
        <div className="barchart-titleBlock">
          <div className="barchart-titleRow">
            <div className="barchart-title">{title}</div>
            {titleRight ? <div className="barchart-titleRight">{titleRight}</div> : null}
          </div>
          {subtitle ? <div className="barchart-subtitle">{subtitle}</div> : null}
        </div>
      )}

      {tipContent}

      <div className="barchart-inner">
        <svg
          ref={svgRef}
          // ✅ Responsive SVG: scales to container, no clipping from padding
          width="100%"
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          className="barchart-svg"
          onMouseMove={onMouseMove}
          onMouseLeave={onLeave}
          onTouchStart={onTouchMove}
          onTouchMove={onTouchMove}
          onTouchEnd={onLeave}
        >
          {/* Axes */}
          <line
            x1={padding.left}
            y1={height - padding.bottom}
            x2={width - padding.right}
            y2={height - padding.bottom}
            className="bc-axis"
          />
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={height - padding.bottom}
            className="bc-axis"
          />

          {/* Y grid + labels */}
          {ticksY.map((t, i) => (
            <g key={i}>
              <line
                x1={padding.left}
                y1={t.y}
                x2={width - padding.right}
                y2={t.y}
                className="bc-grid"
              />
              <text
                x={padding.left - 8}
                y={t.y + 4}
                className="bc-tick"
                textAnchor="end"
              >
                {formatY(t.v)}
              </text>
            </g>
          ))}

          {/* Bars + X labels */}
          {bars.map((b) => (
            <g key={b.i}>
              <rect
                x={b.x}
                y={b.y}
                width={b.w}
                height={Math.max(0, b.h)}
                className={[
                  'bc-bar',
                  hover?.i === b.i ? 'bc-bar-hover' : '',
                ].join(' ')}
                rx="8"
              />
              <text
                x={b.x + b.w / 2}
                y={height - padding.bottom + 18}
                className="bc-tick"
                textAnchor="middle"
              >
                {formatX(b.label)}
              </text>
            </g>
          ))}

          {/* Hover dot */}
          {hover && (
            <circle
              cx={bars[hover.i]?.x + (bars[hover.i]?.w || 0) / 2}
              cy={bars[hover.i]?.y}
              r="4.5"
              className="bc-hover-dot"
            />
          )}
        </svg>
      </div>
    </div>
  );
}
